import { ArrowLeft } from "lucide-react";
import { useCallback, useMemo } from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import { useAuthUser, useRegisterPassword } from "src/auth/useAuth";
import CircleSpinner from "src/components/CircleSpinner";
import ModalBox from "src/components/ModalBox";
import Skeleton from "src/components/Skeleton";
import config from "src/config";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { useStepperWithSearchParams } from "src/hooks/useStepper";
import RegisterIcon from "src/images/buy-property-icon.svg?react";
import LaterIcon from "src/images/later-icon.svg?react";
import { RegisterInnerLayout } from "../Register/Layout";

export function RegisterModalOutlet() {
  const { isLoadingUser, token, user } = useAuthUser();
  const { data: registerLater } = useSessionStorage("register-later");

  // The registration prompt is an overlay, so render it as a sibling of a
  // *always-present* <Outlet/>. Gating the <Outlet/> itself (or swapping which
  // wrapper renders it) remounts the entire routed subtree whenever this
  // condition toggles, which — while the auth query is unsettled — loops and
  // makes every query refetch endlessly. Keeping <Outlet/> structurally stable
  // avoids that.
  // The prompt is for password-less magic-link visitors (e.g. an applicant who
  // clicked an application link and hasn't set a password). Gate directly on
  // whether the account has a password — explicit, rather than inferring it from
  // accountStatus / broker account. `hasPassword` comes from GET /user; while it
  // hasn't loaded yet `isLoadingUser` keeps the modal from flashing.
  const showRegisterModal =
    user?.hasPassword === false && !isLoadingUser && !!token && registerLater !== "true";

  return (
    <>
      {showRegisterModal && <RegisterModal />}
      <Outlet />
    </>
  );
}

const RegisterModal = () => {
  const [, setSearchParams] = useSearchParams();
  const handleClose = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const { user } = useAuthUser();
  // Pull just the register mutation (not the full mutation surface) so this
  // modal stays lightweight and doesn't instantiate the token store etc.
  const { registerPassword, isRegisteringPassword } = useRegisterPassword({
    onSuccessfulRegistration: handleClose,
  });

  const routes = useMemo(
    () => [
      {
        path: "welcome",
        title: `Welcome to ${config.brandName}!`,
        component: <ChooseWhatToDo />,
      },
      {
        path: "register",
        title: "Register your account",
        component: (
          <RegisterInnerLayout
            className="pt-4 px-4"
            isLoading={isRegisteringPassword}
            onSubmit={(submission) => {
              if (!submission?.password) {
                return;
              }
              registerPassword(submission?.password);
            }}
            userEmail={user?.email}
          />
        ),
      },
    ],
    [isRegisteringPassword, registerPassword, user?.email]
  );

  return (
    <ModalBox onClose={handleClose} closeButtonClassName="text-foreground" position="center center" open>
      {user?.id ? (
        <Stepped routes={routes} />
      ) : (
        <div className="flex justify-center items-center h-full w-full bg-card">
          <CircleSpinner />
        </div>
      )}
    </ModalBox>
  );
};

const ChooseWhatToDo = () => {
  const [, setSearchParams] = useSearchParams();
  const { setItem } = useSessionStorage("register-later");
  const { user } = useAuthUser();

  const onClickRegister = useCallback(() => {
    setSearchParams({ step: "register" });
  }, [setSearchParams]);

  const onClickLater = useCallback(() => {
    setItem("true");
    setSearchParams({});
  }, [setSearchParams, setItem]);

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm mb-3">
        You clicked on a link for the email account{" "}
        {user?.email ? (
          <span className="text-green inline">{user?.email}</span>
        ) : (
          <Skeleton inline className="w-20 h-4" />
        )}
        . If you'd like to set up a password for this account, please register it now.
      </p>
      <button
        onClick={onClickRegister}
        className="outline-green w-full p-4 rounded-lg bg-green-50 hover:bg-green-100 dark:hover:bg-green-600 dark:bg-card dark:border-border border border-green-200 flex flex-row items-center gap-3 cursor-pointer"
      >
        <RegisterIcon
          className="md:min-w-[60px] md:min-h-[60px] min-w-[40px] min-h-[40px] hidden md:block"
          height="100%"
        />
        <div className="text-left">
          <h1 className="text-lg font-bold font-serif">Register your account</h1>
          <p className="text-sm font-normal text-green-900 dark:text-foreground">
            Register your account with a password. This will allow you to log in and re-visit your
            application(s).
          </p>
        </div>
      </button>
      <button
        onClick={onClickLater}
        className="outline-green w-full p-4 rounded-lg bg-green-50 hover:bg-green-100 border dark:bg-card dark:hover:bg-green-600 dark:border-border border-green-200 flex flex-row items-center gap-3 cursor-pointer"
      >
        <LaterIcon
          className="md:min-w-[60px] md:min-h-[60px] min-w-[40px] min-h-[40px] hidden md:block"
          height="100%"
        />
        <div className="text-left">
          <h1 className="text-lg font-bold font-serif">Skip for now</h1>
          <p className="text-sm font-normal text-green-900 dark:text-foreground">
            You can always register later by re-visiting the link you clicked to get here.
          </p>
        </div>
      </button>
    </div>
  );
};

const Stepped = ({ routes }: { routes: { path: string; title: string; component: React.ReactNode }[] }) => {
  const { step, previous, ...stepper } = useStepperWithSearchParams(
    routes.map((route) => route.path),
    "welcome"
  );

  return (
    <div className="p-8 py-7">
      <div className="mb-2">
        <h1 className="text-2xl font-serif font-bold">
          {previous ? (
            <button className="p-2 rounded-full bg-card hover:bg-muted mr-1" onClick={stepper.goBack}>
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : null}
          {routes.find((route) => route.path === step)?.title}
        </h1>
      </div>
      {routes.find((route) => route.path === step)?.component}
    </div>
  );
};
